//store
import * as PaymentStore from "../../../../redux/middleware/CashFlow-Payments.Middlerware";
import * as PersonsStore from "../../../../redux/middleware/Persons.Middleware";
import * as PractitionerStore from "../../../../redux/middleware/PractitionerMiddleware";
import * as InvoicesStore from "../../../../redux/middleware/CashFlow-CreditorInvoices.Middleware";
import { creditorKey } from "../../../../redux/states/CreditorInvoicesState";
import { CreditorInvoicesEntry, PaymentAttachmentsOutcome, PaymentAttachmentsRequest } from "../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice";
import { NewPayment } from "../../../../models/Entities/CashFlow/Payment/NewPayment";
import { Settlement } from "../../../../models/Entities/Settlements/Settlement";

// models
import { Payment } from "../../../../models/Entities/CashFlow/Payment/Payment";
import { Debt } from "../../../../models/Entities/CashFlow/Debt/Debt";
import { ErrorType } from "../../../../models/HttpError";
import { AppState } from "../../../../redux/reducers";

import * as jwt_decode from "jwt-decode";
import { Person } from "../../../../models/Entities/Person/Person";
import { Practitioner } from "../../../../models/Entities/Practitioners/Practitioner";
import { buildReceiverOptions, ReceiverOption } from "./Payment.Receiver.Picker";

// form types
export enum FormType {
  payer =1,
  receiver = 2
}

//#region Props

interface IProps {
    /** receiver: el centro cobra. payer: el centro paga. */
    FormType: FormType;
    ReferalId: string | undefined;
    FixedAssociatedEntityIdentifier: string | undefined;
    PaymentId?: number;
    /** Deuda desde la que se registra el cobro/pago: precarga contraparte, moneda, importe e imputación. */
    PrefillDebtId?: number;
    /** Deudas a precargar como objetos (una o varias), para pantallas donde la lista de caja no está cargada. */
    PrefillDebtsData?: Debt[];
    /** Contraparte para arrancar ya elegida. */
    PrefillCounterpartId?: string;
    /**
     * Pago de las deudas de un acreedor (por ejemplo, un profesional) con
     * posibilidad de cambiar el receptor: las deudas candidatas son siempre las
     * de este acreedor y el receptor se elige aparte (por defecto, él mismo).
     * Sin este dato el formulario se comporta como siempre.
     */
    ImputeToCreditorId?: string;
    /** Nombre del acreedor, por si todavía no está en las listas de personas o profesionales. */
    ImputeToCreditorName?: string;
    /** Deudas candidatas ya leídas por la pantalla (no se toman de la caja general). */
    CandidateDebtsData?: Debt[];
    isOpen: boolean;
    onClose: () => void;
  }

  interface ConnectedProps {
    isSaving: boolean;
    isSaved: boolean | undefined;
    failOnSaving: boolean;
    error: ErrorType | undefined;

    CurrentPayment: Payment | undefined;
    OwnerId: string;
    FixedCounterpart: Person | undefined;
    Persons: Person[];
    /** Deudas que este movimiento puede cancelar (a cobrar si es cobro, a pagar si es pago). */
    CandidateDebts: Debt[];
    PrefillDebt: Debt | undefined;
    /** Todas las deudas precargadas; la primera define contraparte y moneda. */
    PrefillDebts: Debt[];
    AllDebts: Debt[];
    AllPayments: Payment[];
    NameOf: (identifier: string | undefined) => string;
    /** Receptores posibles cuando se puede cambiar el receptor (vacío si no). */
    ReceiverOptions: ReceiverOption[];
    /**
     * Receptores posibles de un pago del centro, con el acreedor (`ownerId`) primero como "por defecto":
     * profesionales y personas o empresas (proveedores y sponsors son personas). Vacío si no se conoce.
     */
    ReceiverOptionsFor?: (ownerId: string, ownerName: string) => ReceiverOption[];
    /** El identificador es de un profesional (para ofrecer sus facturas y comprobantes, contrato §12.3). */
    IsPractitioner?: (identifier: string | undefined) => boolean;
    /** Facturas del profesional acreedor (CashFlow §12.2). */
    CreditorInvoicesOf?: (creditorId: string) => CreditorInvoicesEntry | undefined;
    /** Nombre de una liquidación (para las facturas sugeridas). */
    SettlementNameOf?: (settlementId: number) => string;
  }

  interface DispatchProps {
    GetPaymentById: typeof PaymentStore.actionCreators.GetPaymentById;
    /** Conectado devuelve una promesa con el pago creado (undefined si falló). */
    CreatePayment: (payment: NewPayment) => unknown;
    LoadCreditorInvoices?: (creditorId: string) => unknown;
    /** Asocia facturas y sube comprobantes del pago recién creado. */
    AttachToPayment?: (request: PaymentAttachmentsRequest) => Promise<PaymentAttachmentsOutcome>;
    RetryPaymentAttachments?: (request: PaymentAttachmentsRequest, previous: PaymentAttachmentsOutcome) => Promise<PaymentAttachmentsOutcome>;
    UpdatePayment: typeof PaymentStore.actionCreators.UpdatePayment;
    GetAllPersons: typeof PersonsStore.actionCreators.GetAllPersons;
    GetAllPractitioners: typeof PractitionerStore.actionCreators.GetAllPractitioners;
  }

  export type Props = IProps & ConnectedProps & DispatchProps;

export const GetPersonByIdentifier = (persons: Person[], tenantId: string, identifier: string, practitioners: Practitioner[] = []): Person | undefined => {
    if (!identifier) return undefined;
    if (identifier === tenantId) return { personIdentifier: tenantId, businessName: 'Centro Médico' } as Person;
    const person = persons.find((item: Person) => item.personIdentifier === identifier);
    if (person) return person;
    // Un profesional no siempre está en personas: se arma con su nombre.
    const practitioner = practitioners.find((item: Practitioner) => (item.userId || '').toLowerCase() === identifier.toLowerCase());
    return practitioner ? ({ personIdentifier: practitioner.userId, businessName: practitioner.fullName || practitioner.name || identifier } as Person) : undefined;
};

  export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const done: Payment[] = state?.CashFlowPayments?.paymentsDone || [];
    const received: Payment[] = state?.CashFlowPayments?.paymentsReceived || [];
    const currentPayment = ownProps.PaymentId
        ? (ownProps.FormType === FormType.payer ? done : received).find((payment: Payment) => payment.id === ownProps.PaymentId)
            ?? [...done, ...received].find((payment: Payment) => payment.id === ownProps.PaymentId)
        : undefined;

    const currentOwner: string = (jwt_decode.jwtDecode(state?.oidc?.user?.access_token!) as any)?.tenantId;
    const persons = state.Persons?.list || [];
    const practitioners: Practitioner[] = state.Practitioners?.list || [];
    // Liquidaciones conocidas: las de la caja general y, desde el estado de cuenta, las del profesional.
    const settlements: Settlement[] = [
        ...(state.Settlement?.list || []),
        ...((ownProps.ImputeToCreditorId && state.PractitionerAccountStatement?.byPractitioner?.[ownProps.ImputeToCreditorId]?.settlements?.data) || [])
    ];
    const asCreditor: Debt[] = state.CashFlowDebts?.DebtsAsCreditor || [];
    const asDebtor: Debt[] = state.CashFlowDebts?.DebtsAsDebtor || [];
    const stored = ownProps.FormType === FormType.receiver ? asCreditor : asDebtor;
    const byId = ownProps.PrefillDebtId ? stored.find((debt) => debt.id === ownProps.PrefillDebtId) : undefined;
    const prefillList: Debt[] = ownProps.PrefillDebtsData ?? (byId ? [byId] : []);
    // Las deudas precargadas entran aunque la lista de caja no las tenga todavía.
    const missing = prefillList.filter((debt) => !stored.some((item) => item.id === debt.id));
    const candidates = missing.length > 0 ? [...stored, ...missing] : stored;
    // Con deudas propias de la pantalla, las candidatas salen de ahí (y no de la caja general).
    const ownCandidates = ownProps.CandidateDebtsData
        ? [...ownProps.CandidateDebtsData, ...prefillList.filter((debt) => !ownProps.CandidateDebtsData!.some((item) => item.id === debt.id))]
        : undefined;

    return ({
        isSaving: currentPayment ? !!(currentPayment.isUpdating || currentPayment.isSaving) : state.CashFlowPayments?.isAddingNewOne || false,
        isSaved: currentPayment ? (currentPayment.isUpdatedSuccessfully || currentPayment.isSavedSuccessfully) : state.CashFlowPayments?.successAddingNewOne || undefined,
        failOnSaving: currentPayment ? !!(currentPayment.failOnUpdating || currentPayment.failOnSaving) : state.CashFlowPayments?.failOnAddingNewOne || false,
        error: state.CashFlowPayments?.error || currentPayment?.error || undefined,

        CurrentPayment: currentPayment,
        OwnerId: currentOwner,
        FixedCounterpart: GetPersonByIdentifier(persons, currentOwner, ownProps.FixedAssociatedEntityIdentifier || '', practitioners),
        Persons: persons,
        CandidateDebts: ownCandidates ?? candidates,
        PrefillDebt: prefillList[0],
        PrefillDebts: prefillList,
        AllDebts: [...asCreditor, ...asDebtor, ...(ownProps.CandidateDebtsData ?? [])],
        AllPayments: [...received, ...done],
        NameOf: (identifier: string | undefined) => GetPersonByIdentifier(persons, currentOwner, identifier || '', practitioners)?.businessName
            || (identifier && identifier === ownProps.ImputeToCreditorId ? ownProps.ImputeToCreditorName : undefined)
            || identifier || '—',
        ReceiverOptions: ownProps.ImputeToCreditorId
            ? buildReceiverOptions({
                ownerId: ownProps.ImputeToCreditorId,
                ownerName: GetPersonByIdentifier(persons, currentOwner, ownProps.ImputeToCreditorId, practitioners)?.businessName || ownProps.ImputeToCreditorName || ownProps.ImputeToCreditorId,
                practitioners,
                persons,
                excludeIds: [currentOwner]
            })
            : [],
        ReceiverOptionsFor: ownProps.FormType === FormType.payer
            ? (ownerId: string, ownerName: string) => buildReceiverOptions({
                ownerId,
                ownerName: ownerName || GetPersonByIdentifier(persons, currentOwner, ownerId, practitioners)?.businessName || ownerId,
                practitioners,
                persons,
                excludeIds: [currentOwner]
            }).map((option) => (option.id === ownerId && !practitioners.some((item) => (item.userId || '').toLowerCase() === ownerId.toLowerCase())
                // El acreedor puede ser una persona o empresa (proveedor, sponsor), no sólo un profesional.
                ? { ...option, kind: 'person' as const }
                : option))
            : undefined,
        IsPractitioner: (identifier: string | undefined) =>
            !!identifier && (identifier === ownProps.ImputeToCreditorId || practitioners.some((item) => (item.userId || '').toLowerCase() === identifier.toLowerCase())),
        CreditorInvoicesOf: (creditorId: string) => state.CreditorInvoices?.byCreditor?.[creditorKey(creditorId)],
        SettlementNameOf: (settlementId: number) => settlements.find((item) => item.id === settlementId)?.name?.trim() || `LIQ-${settlementId}`
    })
  };

  export const mapDispatchToProps = {
    GetPaymentById: PaymentStore.actionCreators.GetPaymentById,
    CreatePayment: PaymentStore.actionCreators.CreatePayment,
    UpdatePayment: PaymentStore.actionCreators.UpdatePayment,
    LoadCreditorInvoices: InvoicesStore.actionCreators.LoadCreditorInvoices,
    AttachToPayment: InvoicesStore.actionCreators.AttachToPayment,
    RetryPaymentAttachments: InvoicesStore.actionCreators.RetryPaymentAttachments,
    GetAllPersons: PersonsStore.actionCreators.GetAllPersons,
    GetAllPractitioners: PractitionerStore.actionCreators.GetAllPractitioners
  };
